import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router';
import { useAuth } from '../../contexts/AuthContext';
import { Order } from '../../types';
import { ordersApi } from '../../services/api';
import { loadingPlansApi, palletTypesApi } from '../../services/loading-plan-api';
import { PalletPreviewPanel } from '../../components/PalletPreviewPanel';
import { PACKING_ALGORITHMS } from '../../types/loading-plan';
import type { PalletPreviewResponse, LoadingPlanResponse } from '../../types/loading-plan';
import { ArrowLeft, Layers, RefreshCw } from 'lucide-react';

export const OrderPalletPreviewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading: authLoading, login } = useAuth();
  const [autoLoginAttempted, setAutoLoginAttempted] = useState(false);
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [loadingPlan, setLoadingPlan] = useState<LoadingPlanResponse | null>(null);
  const [palletPreview, setPalletPreview] = useState<PalletPreviewResponse | null>(null);
  const [isLoadingPallets, setIsLoadingPallets] = useState(false);
  const [algorithm, setAlgorithm] = useState<string>('ShelfNextFit');
  const [fallbackPalletTypeId, setFallbackPalletTypeId] = useState<string | undefined>(undefined);

  // Auto-login from query params
  useEffect(() => {
    if (autoLoginAttempted || authLoading || isAuthenticated) return;
    const user = searchParams.get('user');
    const password = searchParams.get('password');
    if (user && password) {
      setAutoLoginAttempted(true);
      login({ emailOrUsername: user, password }).catch(() => {
        setError('Auto-login fallido. Credenciales incorrectas.');
        setLoading(false);
      });
    } else if (!isAuthenticated) {
      setError('No autenticado. Añade ?user=xxx&password=xxx a la URL.');
      setLoading(false);
    }
  }, [authLoading, isAuthenticated, autoLoginAttempted, searchParams, login]);

  useEffect(() => {
    if (!isAuthenticated) return;
    palletTypesApi.getAll()
      .then(types => {
        const epal = types.find(t => t.code === 'EPAL 1');
        if (epal) setFallbackPalletTypeId(epal.id);
      })
      .catch(() => {});
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchOrder = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const data = await ordersApi.getById(id);
        setOrder(data as any as Order);
      } catch (err) {
        console.error('Error fetching order:', err);
        setError('No se pudo cargar la comanda');
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [id, isAuthenticated]);

  useEffect(() => {
    if (!order || !id) return;
    const loadPalletData = async () => {
      setIsLoadingPallets(true);
      try {
        const plan = await loadingPlansApi.getByOrder(id);
        if (plan && plan.id) {
          setLoadingPlan(plan);
          setPalletPreview(null);
        } else {
          setLoadingPlan(null);
          await calculatePreview();
        }
      } catch {
        setLoadingPlan(null);
        await calculatePreview();
      } finally {
        setIsLoadingPallets(false);
      }
    };

    const calculatePreview = async () => {
      const validLines = order.lines.filter(l => l.productId && l.quantity > 0);
      const effectivePalletTypeId = order.palletTypeId ?? fallbackPalletTypeId;
      if (!validLines.length || !effectivePalletTypeId) return;
      try {
        const result = await ordersApi.palletPreview({
          lines: validLines.map(l => ({
            productId: l.productId,
            packagingContainerId: l.packagingContainerId ?? null,
            quantity: Math.ceil(l.quantity),
          })),
          algorithm,
          palletTypeId: effectivePalletTypeId,
        });
        setPalletPreview(result);
      } catch {
        // silently fail
      }
    };

    loadPalletData();
  }, [order, id, fallbackPalletTypeId]);

  const handleRecalculate = async () => {
    if (!order) return;
    const validLines = order.lines.filter(l => l.productId && l.quantity > 0);
    const effectivePalletTypeId = order.palletTypeId ?? fallbackPalletTypeId;
    if (!validLines.length || !effectivePalletTypeId) return;
    setIsLoadingPallets(true);
    try {
      const result = await ordersApi.palletPreview({
        lines: validLines.map(l => ({
          productId: l.productId,
          packagingContainerId: l.packagingContainerId ?? null,
          quantity: Math.ceil(l.quantity),
        })),
        algorithm,
        palletTypeId: effectivePalletTypeId,
      });
      setPalletPreview(result);
      setLoadingPlan(null);
    } catch {
      // silently fail
    } finally {
      setIsLoadingPallets(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-green-200 border-t-green-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error || 'Comanda no encontrada'}
        </div>
        <button
          onClick={() => navigate('/comandas')}
          className="mt-4 text-green-600 hover:text-green-700 flex items-center gap-2"
        >
          <ArrowLeft size={20} />
          Volver a comandas
        </button>
      </div>
    );
  }

  const loadingPlanAsPreview: PalletPreviewResponse | null = loadingPlan && loadingPlan.virtualPallets.length > 0
    ? {
        pallets: loadingPlan.virtualPallets.map(vp => ({
          id: vp.id,
          sequenceNumber: vp.sequenceNumber,
          totalBoxes: vp.totalBoxes,
          totalWeightKg: vp.totalWeightKg,
          estimatedHeightCm: vp.estimatedHeightCm,
          items: vp.items.map(i => ({
            productName: i.productName,
            packagingName: i.packagingName,
            numberOfBoxes: i.numberOfBoxes,
            weightKg: i.weightKg,
          })),
          layout: [],
        })),
        totalPallets: loadingPlan.virtualPallets.length,
        warnings: [],
        linesWithoutPackaging: [],
        palletLengthCm: 120,
        palletWidthCm: 80,
        palletBaseHeightCm: 14.4,
      }
    : null;

  const displayPreview = palletPreview || loadingPlanAsPreview;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/comandas/${id}`)}
            className="text-green-600 hover:text-green-700 flex items-center gap-2"
          >
            <ArrowLeft size={20} />
            Volver
          </button>
          <h1 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Layers className="text-green-600" size={20} />
            Preview de Palets — {order.customerName}
            <span className="text-gray-400 font-normal text-sm">#{order.id.slice(0, 8)}</span>
          </h1>
        </div>
      </div>

      {/* Algorithm selector */}
      {!loadingPlan && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <label className="block text-xs font-medium text-slate-500 mb-1">Algoritmo de montaje</label>
          <div className="flex gap-2">
            <select
              value={algorithm}
              onChange={(e) => setAlgorithm(e.target.value)}
              className="flex-1 bg-white border border-green-200 text-slate-900 text-xs rounded-md p-2"
            >
              {PACKING_ALGORITHMS.map(a => (
                <option key={a.key} value={a.key}>{a.label}</option>
              ))}
            </select>
            <button
              onClick={handleRecalculate}
              disabled={isLoadingPallets}
              className="px-3 py-2 border border-green-200 rounded-md text-green-700 hover:bg-green-50 text-xs flex items-center gap-1"
            >
              <RefreshCw className={`h-3 w-3 ${isLoadingPallets ? 'animate-spin' : ''}`} />
              Recalcular
            </button>
          </div>
        </div>
      )}

      {/* Pallet preview */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
        {isLoadingPallets && (
          <div className="flex items-center justify-center py-8">
            <div className="w-8 h-8 border-3 border-green-200 border-t-green-600 rounded-full animate-spin"></div>
          </div>
        )}
        {!isLoadingPallets && displayPreview && (
          <PalletPreviewPanel preview={displayPreview} />
        )}
        {!isLoadingPallets && !displayPreview && (
          <p className="text-xs text-slate-400 italic py-4 text-center">
            No hay datos de palets disponibles para este pedido.
          </p>
        )}
      </div>
    </div>
  );
};
